import { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Clock3, ListChecks, Swords, Users } from "lucide-react";
import { getExamById, getPrivateChallenge, joinPrivateChallenge } from "utils/supabase";
import { linkChallengeInviteCode } from "utils/challengeLink";
import BackdropAlert from "components/Custom/BackdropAlert.js";

export default function RetoInvite() {
  const { inviteCode } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [examStatus, setExamStatus] = useState(null);
  const [isJoining, setIsJoining] = useState(false);
  const [backdropAlert, setBackdropAlert] = useState({
    open: false,
    severity: "info",
    title: "",
    message: "",
    closeOnBackdropClick: true,
    primaryAction: null,
  });

  const closeBackdropAlert = useCallback(() => {
    setBackdropAlert((prev) => ({ ...prev, open: false }));
  }, []);

  const showBackdropAlert = useCallback(({
    severity = "info",
    title = "",
    message = "",
    closeOnBackdropClick = true,
    primaryAction = null,
  }) => {
    setBackdropAlert({ open: true, severity, title, message, closeOnBackdropClick, primaryAction });
  }, []);

  useEffect(() => {
    async function fetchChallenge() {
      setLoading(true);
      try {
        const { data, error } = await getPrivateChallenge(inviteCode);
        if (error || !data?.success) {
          throw new Error(error?.message || "No se encontro el reto.");
        }

        setChallenge(data.challenge);

        // Solo sabemos si ya se completo el examen consultando el examen directamente.
        if (data.challenge?.already_joined && data.challenge?.exam_id) {
          const { exam } = await getExamById(data.challenge.exam_id);
          setExamStatus(String(exam?.status || "").toLowerCase());
        }
      } catch (error) {
        console.error("Error fetching private challenge:", error);
        showBackdropAlert({
          severity: "error",
          title: "No se pudo cargar el reto",
          message: error?.message || "El enlace puede ser invalido o haber expirado.",
          closeOnBackdropClick: false,
          primaryAction: { label: "Volver al dashboard", onClick: () => history.push("/admin/dashboard") },
        });
      } finally {
        setLoading(false);
      }
    }

    if (inviteCode) fetchChallenge();
  }, [inviteCode, history, showBackdropAlert]);

  const handleAccept = async () => {
    if (isJoining) return;

    setIsJoining(true);
    try {
      const { data, error } = await joinPrivateChallenge(inviteCode);
      if (error || !data?.success || !data?.exam_id) {
        throw new Error(error?.message || "No se pudo unir al reto.");
      }
      linkChallengeInviteCode(data.exam_id, inviteCode);
      history.push(`/admin/simulador/${data.exam_id}`);
    } catch (error) {
      console.error("Error joining private challenge:", error);
      showBackdropAlert({
        severity: "error",
        title: "No se pudo unir al reto",
        message: error?.message || "Ocurrio un error inesperado.",
      });
    } finally {
      setIsJoining(false);
    }
  };

  const handleViewResult = () => {
    if (examStatus === "completed") {
      history.push(`/admin/reto/${inviteCode}/resultados`);
    } else if (challenge?.exam_id) {
      linkChallengeInviteCode(challenge.exam_id, inviteCode);
      history.push(`/admin/simulador/${challenge.exam_id}`);
    }
  };

  const alreadyJoined = Boolean(challenge?.already_joined);
  const isCompleted = alreadyJoined && examStatus === "completed";
  const isExpired =
    Boolean(challenge) &&
    (challenge.status !== "active" ||
      (challenge.expires_at && new Date(challenge.expires_at) < new Date()));

  return (
    <div className="flex flex-wrap mt-100 justify-center">
      <div className="w-full lg:w-6/12 px-4">
        <Paper
          elevation={0}
          sx={{ border: "1px solid #e5e7eb", borderRadius: "16px", p: { xs: 2.5, sm: 4 }, textAlign: "center" }}
        >
          {loading ? (
            <Stack spacing={2} alignItems="center">
              <Skeleton variant="circular" width={64} height={64} />
              <Skeleton variant="text" width="70%" height={32} />
              <Skeleton variant="text" width="50%" height={24} />
              <Skeleton variant="rectangular" width="100%" height={48} sx={{ borderRadius: "10px" }} />
            </Stack>
          ) : challenge ? (
            <>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "9999px",
                  bgcolor: "#ede9fe",
                  color: "#7c3aed",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <Swords size={30} />
              </Box>

              <Typography sx={{ fontSize: 15, color: "#4b5563", fontWeight: 600 }}>
                ⚔️ {challenge.creator?.display_name || "Un jugador"} te ha retado
              </Typography>

              <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#111827", mt: 0.5, mb: 2 }}>
                {challenge.title}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignItems: "center",
                  columnGap: 1,
                  rowGap: 1,
                  width: "100%",
                  mb: 3,
                }}
              >
                <Chip
                  icon={<ListChecks size={16} color="#2563eb" />}
                  label={`${challenge.question_count} preguntas`}
                  sx={{ fontWeight: 600, bgcolor: "#eff6ff", color: "#1d4ed8" }}
                />
                <Chip
                  icon={<Clock3 size={16} color="#c2410c" />}
                  label={
                    challenge.duration_minutes ? (
                      `${challenge.duration_minutes} minutos`
                    ) : (
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        <Box component="span" sx={{ fontSize: 26, lineHeight: 1 }}>∞</Box> minutos
                      </Box>
                    )
                  }
                  sx={{ fontWeight: 600, bgcolor: "#fff7ed", color: "#c2410c" }}
                />
                <Chip
                  icon={<Users size={16} color="#15803d" />}
                  label={`${challenge.participants_count} participantes`}
                  sx={{ fontWeight: 600, bgcolor: "#ecfdf5", color: "#15803d" }}
                />
              </Box>

              {isExpired ? (
                <Typography sx={{ color: "#dc2626", fontWeight: 600 }}>
                  Este reto ya no esta disponible.
                </Typography>
              ) : (
                <Button
                  variant="contained"
                  onClick={alreadyJoined ? handleViewResult : handleAccept}
                  disabled={isJoining}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "10px",
                    py: 1.2,
                    px: 4,
                    bgcolor: "#7c3aed",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#6d28d9", boxShadow: "none" },
                  }}
                >
                  {isJoining ? (
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  ) : alreadyJoined ? (
                    isCompleted ? "Ver resultado" : "Continuar reto"
                  ) : (
                    "Aceptar reto"
                  )}
                </Button>
              )}
            </>
          ) : null}
        </Paper>
      </div>

      <BackdropAlert
        open={backdropAlert.open}
        onClose={closeBackdropAlert}
        severity={backdropAlert.severity}
        title={backdropAlert.title}
        message={backdropAlert.message}
        closeOnBackdropClick={backdropAlert.closeOnBackdropClick}
        primaryAction={backdropAlert.primaryAction}
      />
    </div>
  );
}
